import { billingApi } from '@/api/billing.api';
import { crmApi } from '@/api/crm.api';
import { customersApi } from '@/api/customers.api';
import { employeesApi } from '@/api/employees.api';
import { hrApi } from '@/api/hr.api';
import { productsApi } from '@/api/products.api';
import { storesApi } from '@/api/stores.api';
import type { ErpModule } from '@/api/platform.api';
import type { PageResponse } from '@/types/api';
import { matchesVisitPrefix, TOUR_STEPS } from '@/core/tour/tourConfig';

function hasRecords(page: PageResponse<unknown> | null | undefined): boolean {
  if (!page) return false;
  return (page.totalElements ?? page.content?.length ?? 0) > 0;
}

function visitCompletion(pathname: string, module: ErpModule): Record<string, boolean> {
  const steps = TOUR_STEPS[module] ?? [];
  const result: Record<string, boolean> = {};
  for (const step of steps) {
    if (matchesVisitPrefix(pathname, step.visitPrefixes)) {
      result[step.id] = true;
    }
  }
  return result;
}

async function detectPosProgress(pathname: string): Promise<Record<string, boolean>> {
  const [stores, products, customers, invoices] = await Promise.all([
    storesApi.list({ size: 1 }).catch(() => null),
    productsApi.list({ size: 1 }).catch(() => null),
    customersApi.list({ size: 1 }).catch(() => null),
    billingApi.listInvoices({ size: 1 }).catch(() => null),
  ]);

  return {
    store: hasRecords(stores),
    product: hasRecords(products),
    customer: hasRecords(customers),
    sale: hasRecords(invoices),
    ...visitCompletion(pathname, 'POS'),
  };
}

async function detectCrmProgress(pathname: string): Promise<Record<string, boolean>> {
  const [leadsPage, dealsPage] = await Promise.all([
    crmApi.listLeads({ size: 20 }).catch(() => null),
    crmApi.listDeals({ size: 1 }).catch(() => null),
  ]);

  const leads = leadsPage?.content ?? [];
  const hasLead = hasRecords(leadsPage);
  const hasConverted = leads.some((lead) => lead.converted);
  const hasDeal = hasRecords(dealsPage);

  return {
    lead: hasLead,
    convert: hasConverted,
    deal: hasDeal,
    ...visitCompletion(pathname, 'CRM'),
  };
}

async function detectHrProgress(pathname: string): Promise<Record<string, boolean>> {
  const [employees, attendance, payrollRuns] = await Promise.all([
    employeesApi.list({ size: 1 }).catch(() => null),
    hrApi.listAttendance({ size: 1 }).catch(() => null),
    hrApi.listPayrollRuns({ size: 1 }).catch(() => null),
  ]);

  return {
    employee: hasRecords(employees),
    attendance: hasRecords(attendance),
    payroll: hasRecords(payrollRuns),
    ...visitCompletion(pathname, 'HR'),
  };
}

export async function detectTourProgress(
  module: ErpModule,
  pathname: string,
): Promise<Record<string, boolean>> {
  switch (module) {
    case 'POS':
      return detectPosProgress(pathname);
    case 'CRM':
      return detectCrmProgress(pathname);
    case 'HR':
      return detectHrProgress(pathname);
    default:
      return visitCompletion(pathname, module);
  }
}
